/**
 * Physical keyboard the practice runs on. Every key is named by its
 * `KeyboardEvent.code` — the position of the key, not the letter printed on it —
 * so a Bijoy lesson works the same whether the computer's own keyboard is set to
 * English, Bijoy or anything else: the tool reads which key went down and decides
 * itself what that key types in the chosen layout.
 *
 * Widths are in key units (1u = one letter key) and follow a standard ANSI board.
 */

/** @typedef {'lp'|'lr'|'lm'|'li'|'ri'|'rm'|'rr'|'rp'|'th'} Finger */

export const KEY_ROWS = [
    [
        { code: 'Backquote' }, { code: 'Digit1' }, { code: 'Digit2' }, { code: 'Digit3' },
        { code: 'Digit4' }, { code: 'Digit5' }, { code: 'Digit6' }, { code: 'Digit7' },
        { code: 'Digit8' }, { code: 'Digit9' }, { code: 'Digit0' }, { code: 'Minus' },
        { code: 'Equal' }, { code: 'Backspace', w: 2, label: 'Backspace', modifier: true },
    ],
    [
        { code: 'Tab', w: 1.5, label: 'Tab', modifier: true },
        { code: 'KeyQ' }, { code: 'KeyW' }, { code: 'KeyE' }, { code: 'KeyR' }, { code: 'KeyT' },
        { code: 'KeyY' }, { code: 'KeyU' }, { code: 'KeyI' }, { code: 'KeyO' }, { code: 'KeyP' },
        { code: 'BracketLeft' }, { code: 'BracketRight' }, { code: 'Backslash', w: 1.5 },
    ],
    [
        { code: 'CapsLock', w: 1.75, label: 'Caps', modifier: true },
        { code: 'KeyA', home: true }, { code: 'KeyS', home: true }, { code: 'KeyD', home: true },
        { code: 'KeyF', home: true, bump: true }, { code: 'KeyG' }, { code: 'KeyH' },
        { code: 'KeyJ', home: true, bump: true }, { code: 'KeyK', home: true }, { code: 'KeyL', home: true },
        { code: 'Semicolon', home: true }, { code: 'Quote' },
        { code: 'Enter', w: 2.25, label: 'Enter', modifier: true },
    ],
    [
        { code: 'ShiftLeft', w: 2.25, label: 'Shift', modifier: true },
        { code: 'KeyZ' }, { code: 'KeyX' }, { code: 'KeyC' }, { code: 'KeyV' }, { code: 'KeyB' },
        { code: 'KeyN' }, { code: 'KeyM' }, { code: 'Comma' }, { code: 'Period' }, { code: 'Slash' },
        { code: 'ShiftRight', w: 2.75, label: 'Shift', modifier: true },
    ],
    [
        { code: 'ControlLeft', w: 1.25, label: 'Ctrl', modifier: true, inert: true },
        { code: 'MetaLeft', w: 1.25, label: '', modifier: true, inert: true },
        { code: 'AltLeft', w: 1.25, label: 'Alt', modifier: true, inert: true },
        { code: 'Space', w: 6.25, label: '', modifier: true },
        { code: 'AltRight', w: 1.25, label: 'Alt', modifier: true, inert: true },
        { code: 'MetaRight', w: 1.25, label: '', modifier: true, inert: true },
        { code: 'ContextMenu', w: 1.25, label: '', modifier: true, inert: true },
        { code: 'ControlRight', w: 1.25, label: 'Ctrl', modifier: true, inert: true },
    ],
];

/**
 * Standard touch-typing finger for every key (the chart TypingClub, typing.com and
 * Ratatype all teach). 5 belongs to the left index and 6 to the right index.
 * @type {Record<string, Finger>}
 */
export const FINGER_OF = {
    Backquote: 'lp', Digit1: 'lp', Tab: 'lp', KeyQ: 'lp', CapsLock: 'lp', KeyA: 'lp', ShiftLeft: 'lp', KeyZ: 'lp',
    Digit2: 'lr', KeyW: 'lr', KeyS: 'lr', KeyX: 'lr',
    Digit3: 'lm', KeyE: 'lm', KeyD: 'lm', KeyC: 'lm',
    Digit4: 'li', Digit5: 'li', KeyR: 'li', KeyT: 'li', KeyF: 'li', KeyG: 'li', KeyV: 'li', KeyB: 'li',
    Digit6: 'ri', Digit7: 'ri', KeyY: 'ri', KeyU: 'ri', KeyH: 'ri', KeyJ: 'ri', KeyN: 'ri', KeyM: 'ri',
    Digit8: 'rm', KeyI: 'rm', KeyK: 'rm', Comma: 'rm',
    Digit9: 'rr', KeyO: 'rr', KeyL: 'rr', Period: 'rr',
    Digit0: 'rp', Minus: 'rp', Equal: 'rp', Backspace: 'rp', KeyP: 'rp', BracketLeft: 'rp',
    BracketRight: 'rp', Backslash: 'rp', Semicolon: 'rp', Quote: 'rp', Enter: 'rp', Slash: 'rp',
    ShiftRight: 'rp',
    Space: 'th',
};

/** Home-row key each finger rests on — the hand guide draws the reach from here. */
export const HOME_KEY_OF_FINGER = {
    lp: 'KeyA', lr: 'KeyS', lm: 'KeyD', li: 'KeyF', ri: 'KeyJ', rm: 'KeyK', rr: 'KeyL', rp: 'Semicolon', th: 'Space',
};

export const FINGERS = ['lp', 'lr', 'lm', 'li', 'th', 'ri', 'rm', 'rr', 'rp'];

export const handOf = (code) => {
    const finger = FINGER_OF[code];
    if (!finger || finger === 'th') return null;
    return finger[0] === 'l' ? 'left' : 'right';
};

/**
 * Shift is pressed by the little finger of the OTHER hand, so the typing hand stays
 * on its row: a capital on the left half takes the right Shift and vice versa.
 */
export const shiftCodeFor = (code) => (handOf(code) === 'left' ? 'ShiftRight' : 'ShiftLeft');

export const isShiftCode = (code) => code === 'ShiftLeft' || code === 'ShiftRight';

/** Every key a layout can put a character on (the inert modifiers never type). */
export const TYPING_CODES = KEY_ROWS.flat()
    .filter((key) => !key.modifier || key.code === 'Space')
    .map((key) => key.code);

/** Plain US-QWERTY legend: code → [normal, shifted]. */
export const QWERTY = {
    Backquote: ['`', '~'], Digit1: ['1', '!'], Digit2: ['2', '@'], Digit3: ['3', '#'], Digit4: ['4', '$'],
    Digit5: ['5', '%'], Digit6: ['6', '^'], Digit7: ['7', '&'], Digit8: ['8', '*'], Digit9: ['9', '('],
    Digit0: ['0', ')'], Minus: ['-', '_'], Equal: ['=', '+'],
    KeyQ: ['q', 'Q'], KeyW: ['w', 'W'], KeyE: ['e', 'E'], KeyR: ['r', 'R'], KeyT: ['t', 'T'],
    KeyY: ['y', 'Y'], KeyU: ['u', 'U'], KeyI: ['i', 'I'], KeyO: ['o', 'O'], KeyP: ['p', 'P'],
    BracketLeft: ['[', '{'], BracketRight: [']', '}'], Backslash: ['\\', '|'],
    KeyA: ['a', 'A'], KeyS: ['s', 'S'], KeyD: ['d', 'D'], KeyF: ['f', 'F'], KeyG: ['g', 'G'],
    KeyH: ['h', 'H'], KeyJ: ['j', 'J'], KeyK: ['k', 'K'], KeyL: ['l', 'L'], Semicolon: [';', ':'],
    Quote: ["'", '"'],
    KeyZ: ['z', 'Z'], KeyX: ['x', 'X'], KeyC: ['c', 'C'], KeyV: ['v', 'V'], KeyB: ['b', 'B'],
    KeyN: ['n', 'N'], KeyM: ['m', 'M'], Comma: [',', '<'], Period: ['.', '>'], Slash: ['/', '?'],
    Space: [' ', ' '],
};

/** Reverse of QWERTY: printable ASCII → { code, shift }. */
export const QWERTY_STROKE_OF = (() => {
    const map = {};
    Object.entries(QWERTY).forEach(([code, [plain, shifted]]) => {
        if (!(plain in map)) map[plain] = { code, shift: false };
        if (!(shifted in map)) map[shifted] = { code, shift: true };
    });
    return map;
})();
